import {addDays, fmtMDY} from '../lib/dateUtils.js';
import {calcBreedingTimeline, cycleLabel} from '../lib/pig.js';
import type {PigBreedingCycle, PigBreedingTimeline} from '../lib/pig.js';
import type {FarrowingCountsRecord} from '../lib/pigFarrowBatch.js';

// Shared by Farrowing (per-cycle "not yet farrowed") and Breeding Pigs
// ("Sows missing multiple cycles"), so both read one definition of a miss.
export type MissedSow = {tag: string; status: 'missed' | 'pending' | 'not_yet'; cycleLabel: string};
export type MissedSowRollup = {tag: string; cycles: string[]; lastFarrow: string | null | undefined};

export function farrowingRecordsForCycle<T extends FarrowingCountsRecord>(cycle: PigBreedingCycle, records: T[]): T[] {
  const timeline = calcBreedingTimeline(cycle.exposureStart);
  if (!timeline) return [];
  return records
    .filter((row) => {
      if (row.group !== cycle.group || !row.farrowingDate) return false;
      const day = new Date(row.farrowingDate + 'T12:00:00');
      return day >= new Date(timeline.farrowingStart + 'T12:00:00') && day <= addDays(timeline.farrowingEnd, 14);
    })
    .sort((a, b) => (a.farrowingDate || '').localeCompare(b.farrowingDate || ''));
}

export function missedSowsForCycle(
  cycle: PigBreedingCycle,
  records: FarrowingCountsRecord[],
  timeline: PigBreedingTimeline,
  cycleSeqMap: Record<string, string>,
): MissedSow[] {
  const tags = [...(cycle.boar1Tags || '').split(/[\s,]+/), ...(cycle.boar2Tags || '').split(/[\s,]+/)]
    .map((tag) => tag.trim())
    .filter(Boolean);
  const farrowed = new Set(records.map((row) => String(row.sow ?? '').trim()));
  const today = new Date();
  const status =
    today > new Date(timeline.farrowingEnd + 'T12:00:00')
      ? 'missed'
      : today >= new Date(timeline.farrowingStart + 'T12:00:00')
        ? 'pending'
        : 'not_yet';
  return tags
    .filter((tag) => !farrowed.has(tag))
    .map((tag) => ({
      tag,
      status,
      cycleLabel: `${cycleLabel(cycle, cycleSeqMap)} (${fmtMDY(timeline.farrowingStart)} – ${fmtMDY(timeline.farrowingEnd)})`,
    }));
}

export function sowsMissingMultipleCycles(
  cycles: PigBreedingCycle[],
  records: FarrowingCountsRecord[],
  cycleSeqMap: Record<string, string>,
): MissedSowRollup[] {
  const sortedCycles = [...cycles].sort((a, b) => (b.exposureStart || '').localeCompare(a.exposureStart || ''));
  const missMap: Record<string, MissedSowRollup> = {};
  sortedCycles.forEach((cycle) => {
    const timeline = calcBreedingTimeline(cycle.exposureStart);
    if (!timeline) return;
    missedSowsForCycle(cycle, farrowingRecordsForCycle(cycle, records), timeline, cycleSeqMap)
      .filter((sow) => sow.status === 'missed')
      .forEach((sow) => {
        if (!missMap[sow.tag]) missMap[sow.tag] = {tag: sow.tag, cycles: [], lastFarrow: null};
        missMap[sow.tag].cycles.push(sow.cycleLabel);
      });
  });
  Object.values(missMap).forEach((sow) => {
    sow.lastFarrow =
      records
        .filter((row) => String(row.sow ?? '').trim() === sow.tag && row.farrowingDate)
        .sort((a, b) => (b.farrowingDate || '').localeCompare(a.farrowingDate || ''))[0]?.farrowingDate || null;
  });
  return Object.values(missMap)
    .filter((sow) => sow.cycles.length > 1)
    .sort((a, b) => b.cycles.length - a.cycles.length);
}
